/* global turf */
/* global polyline */
import { parseFeatureMetadata } from '../helpers/waypointHelper';
import {
    geoCodePoints,
    getRegionsAndMountains,
    minimizeCoordinates,
    minimizeCoordinate,
    decodeWpGeoJSON2Line,
    calculateBearing,
} from '../helpers/geoSpatialHelper';
import { enrichLine } from '../helpers/trailHelper';
import { enrichPoint } from '../helpers/waypointHelper';
import { last, first, containsWords, getUUID } from '../helpers/commonHelper';
import {
    defaultGeoFeatureProperties,
    processingParameters,
} from '../constants/constants';

export const getLines = (collection) => {
    if (!collection || !collection.features) {
        return [];
    }
    return collection.features.filter(
        (f) => f.geometry.type.indexOf('Line') === 0,
    );
};

export const getPoints = (collection) => {
    if (!collection || !collection.features) {
        return [];
    }
    return collection.features.filter((f) => f.geometry.type === 'Point');
};

export const getStatistics = (collection) => {
    const allLines = collection.features.filter(
        (f) => f.geometry.type.indexOf('Line') === 0,
    );
    const totalLines = allLines.length;
    const totalCoordinatesByLine = allLines.map(
        (l) => l.geometry.coordinates.length,
    );
    const totalPoints = collection.features.filter(
        (f) => f.geometry.type === 'Point',
    ).length;
    return {
        totalPoints,
        totalLines,
        totalCoordinatesByLine,
    };
};

export const reParseCollection = (collection, state) => {
    const newCollection = { ...collection };
    // Initial parse
    newCollection.features = newCollection.features.map((f) => {
        if (f.properties.id) {
            return { ...f };
        } else {
            return {
                ...f,
                properties: parseFeatureMetadata(f),
            };
        }
    });
    // Get regions and mountains
    const placeObjects = getRegionsAndMountains(newCollection, state);
    const lines = getLines(newCollection);
    lines.forEach((line) => {
        if (
            !line.properties.placeObjects ||
            !line.properties.placeObjects.length
        ) {
            line.properties.placeObjects = placeObjects;
        }
        line.geometry.coordinates = minimizeCoordinates(
            line.geometry.coordinates,
        );
    });
    const points = getPoints(newCollection);
    points.forEach((point) => {
        point.geometry.coordinates = minimizeCoordinate(
            point.geometry.coordinates,
        );
        point.properties.bearing = calculateBearing(point, lines);
    });
    // Geo code waypoints
    geoCodePoints(newCollection, state);
    return newCollection;
};

export const parseCollection = (collection, state) => {
    const newCollection = { ...collection };
    // Initial parse
    newCollection.features = newCollection.features.map((f) => {
        return {
            ...f,
            properties: parseFeatureMetadata(f),
        };
    });
    // Get regions and mountains
    const placeObjects = getRegionsAndMountains(newCollection, state);
    const lines = getLines(newCollection);
    lines.forEach((line) => {
        if (!line.properties.placeObjects.length) {
            line.properties.placeObjects = placeObjects;
        }
        line.geometry.coordinates = minimizeCoordinates(
            line.geometry.coordinates,
        );
    });
    const points = getPoints(newCollection);
    points.forEach((point) => {
        point.geometry.coordinates = minimizeCoordinate(
            point.geometry.coordinates,
        );
        point.properties.bearing = calculateBearing(point, lines);
    });
    // Geo code waypoints
    geoCodePoints(newCollection, state);
    return newCollection;
};

export const appendCoordinate2Collection = (coordinate, state) => {
    const lastCollection =
        last(state.collectionStack) || turf.featureCollection([]);
    const existingCollection = JSON.parse(JSON.stringify(lastCollection));
    const existingLines = getLines(existingCollection);
    if (!existingLines.length) {
        return null;
    }
    let updated = false;
    existingLines.forEach((line) => {
        const { geometry } = line;
        let { coordinates } = geometry;
        const lastExistingCoordinate = last(coordinates);
        const distance2Last = turf.distance(
            turf.point(lastExistingCoordinate),
            turf.point(coordinate),
        );
        if (distance2Last < processingParameters.offRoadPointTreshold) {
            coordinates.push(coordinate);
            updated = true;
        } else {
            console.warn(
                `Clicked point too far (${distance2Last}) from existing trail`,
            );
        }
    });
    if (!updated) {
        return false;
    }
    return existingCollection;
};

export const parseDrawnPath2Collection = (path, state) => {
    if (!path['points_encoded']) {
        console.error('Points not encoded. Check out the log');
        console.log(path);
        return false;
    }
    const { points, instructions, distance } = path;
    const legDistanceKms = Math.round(distance / 10) / 100;
    let currentTotalDistance = 0;
    // console.log(
    //     `snapped_waypoints = ${JSON.stringify(
    //         polyline.decode(path['snapped_waypoints']),
    //     )}`,
    // );
    // Parse path into line
    const newSegmentCoordinates = polyline
        .decode(points)
        .map((c) => [c[1], c[0]]);
    const newLine = turf.lineString(newSegmentCoordinates, {
        isDraw: true,
        distance: legDistanceKms,
        surfaceCollection:
            defaultGeoFeatureProperties.LineString.surfaceCollection,
    });
    const firstNewSegmentCoordinate = first(newSegmentCoordinates);
    // Existing collection and line append
    const lastCollection =
        last(state.collectionStack) || turf.featureCollection([]);
    const existingCollection = JSON.parse(JSON.stringify(lastCollection));
    // Appending or adding lines
    const existingLines = getLines(existingCollection);
    if (!existingLines.length) {
        existingCollection.features.push(newLine);
    } else if (existingLines.length > 1) {
        console.error('More than 1 active line!!!');
    } else {
        existingLines.forEach((line) => {
            currentTotalDistance = turf.length(line);
            const { geometry } = line;
            let { coordinates } = geometry;
            const lastExistingCoordinate = last(coordinates);
            const distanceLast2First = turf.distance(
                turf.point(lastExistingCoordinate),
                turf.point(firstNewSegmentCoordinate),
            );
            if (
                distanceLast2First < processingParameters.stickyPointsTreshold
            ) {
                line.geometry.coordinates = [
                    ...coordinates,
                    ...newSegmentCoordinates,
                ];
                line.properties.distance += legDistanceKms;
            } else {
                // Unwanted situation....
                // existingCollection.features.push(newLine);
                console.warn(
                    'Distance bewtween old and new line too far, adding another line to collection',
                );
            }
        });
    }
    const trailLine = getLines(existingCollection)[0];
    let surface = 'A';
    instructions
        .sort((a, b) => a.distance - b.distance)
        .forEach((instruction) => {
            const {
                coordinate,
                distance, // in meters
                name, // just name
                text, // Directions. Parse 'Nastavite' & 'Skrenite' with 'lijevo', 'desno' + 'oštro'
                annotations, // Surface transitions: paved, unpaved, small_way
                pushbike, // Slope and terrain indication
                speed, // Can indicate the surface & slope: 10 - flat paved, 2 - mild steep gravel, 16 - downhill
            } = instruction;
            const isTurn = containsWords(text, ['turn', 'skrenite']);
            const isKeep = containsWords(text, ['keep', 'nastavite']);
            const isLeft = containsWords(text, ['left', 'lijev']);
            const isRight = containsWords(text, ['right', 'desn']);
            const isHard = containsWords(text, ['hard', 'oštro']);
            const isEasy = containsWords(text, ['blago', 'easy']);
            const odometer =
                Math.round(
                    (currentTotalDistance +
                        Math.round((distance || 0) / 10) / 100) *
                        100,
                ) / 100;
            // Surface calculation
            let surfaceChange = false;
            let newSurface = 'A';
            if (annotations.some((e) => e === 'unpaved')) {
                newSurface = 'M';
            }
            if (annotations.some((e) => e === 'off_bike')) {
                newSurface = speed > 3 ? 'S' : 'P';
            }
            if (newSurface !== surface) {
                surface = newSurface;
                surfaceChange = true;
                const existingSurfaceTransition = trailLine.properties.surfaceCollection.find(
                    (e) => e.key === odometer,
                );
                if (existingSurfaceTransition) {
                    existingSurfaceTransition.value = surface;
                } else {
                    trailLine.properties.surfaceCollection.push({
                        key: odometer,
                        value: surface,
                    });
                }
            }
            // Pictogram parsing
            const pictogramDrawn = [];
            if (isTurn) {
                if (isHard) {
                    if (isLeft) {
                        pictogramDrawn.push(225);
                    }
                    if (isRight) {
                        pictogramDrawn.push(315);
                    }
                } else if (isEasy) {
                    if (isLeft) {
                        pictogramDrawn.push(135);
                    }
                    if (isRight) {
                        pictogramDrawn.push(45);
                    }
                } else {
                    if (isLeft) {
                        pictogramDrawn.push(180);
                    }
                    if (isRight) {
                        pictogramDrawn.push(0);
                    }
                }
            }
            if (isKeep) {
                if (isHard) {
                    if (isLeft) {
                        pictogramDrawn.push(225);
                        pictogramDrawn.push(90);
                    }
                    if (isRight) {
                        pictogramDrawn.push(315);
                        pictogramDrawn.push(90);
                    }
                } else if (isEasy) {
                    if (isLeft) {
                        pictogramDrawn.push(135);
                        pictogramDrawn.push(45);
                    }
                    if (isRight) {
                        pictogramDrawn.push(45);
                        pictogramDrawn.push(135);
                    }
                } else {
                    if (isLeft) {
                        pictogramDrawn.push(180);
                        pictogramDrawn.push(45);
                    }
                    if (isRight) {
                        pictogramDrawn.push(0);
                        pictogramDrawn.push(135);
                    }
                }
            }
            const newPoint = turf.point([coordinate[1], coordinate[0]], {
                name,
                nameEn: name,
                odometer,
                desc: text,
                isDraw: true,
                annotations,
                pushbike,
                speed,
                pictogramDrawn: pictogramDrawn.join('-'),
            });
            if (!surfaceChange) {
                existingCollection.features.push(newPoint);
            }
        });
    return existingCollection;
};

export const getAllCoordinates = (collection) => {
    let coordinates = [];
    const points = getPoints(collection);
    const lines = getLines(collection);
    points.forEach((p) => {
        coordinates.push(p.geometry.coordinates);
    });
    lines.forEach((l) => {
        coordinates = [...coordinates, ...l.geometry.coordinates];
    });
    return coordinates.map((c) => {
        return {
            lng: c[0],
            lat: c[1],
        };
    });
};

export const getEnrichedCollection = (collection) => {
    const lines = getLines(collection).map((line) => enrichLine(line));
    let points = getPoints(collection);
    let newCollection = {
        ...collection,
        features: [...lines, ...points],
    };
    points = points.map((point) => enrichPoint(point, newCollection));
    newCollection = {
        ...newCollection,
        features: [...lines, ...points],
    };
    return {
        ...newCollection,
        features: [...lines, ...points],
    };
};

export const getHomeCollection = (trails, uuid) => {
    const features = [];
    trails.forEach((trail) => {
        const trailHasThePlace = trail.placeObjects.some(
            (p) => p.uuid === uuid,
        );
        const isPlaceSearch = uuid && trailHasThePlace;
        if (!uuid || isPlaceSearch) {
            const newPoint = turf.point(trail.center, {
                ...trail,
                isHome: true,
            });
            features.push(newPoint);
            if (trail.wpGeoJSON) {
                features.push(decodeWpGeoJSON2Line(trail.wpGeoJSON));
            }
        }
    });
    return turf.featureCollection(features);
};

export const filterAndSortCollection = (collection, sortRule) => {
    if (!collection) {
        return false;
    }
    if (!sortRule) {
        return {
            ...collection,
            features: collection.features.filter(
                (e) => e.properties.name !== undefined,
            ),
        };
    }
    const { featureKey, ascending } = sortRule;
    return {
        ...collection,
        features: collection.features
            .filter((e) => e.properties[featureKey] !== undefined)
            .sort((a, b) => {
                let result = 0;
                if (a.properties[featureKey] > b.properties[featureKey]) {
                    result = 1;
                }
                if (a.properties[featureKey] < b.properties[featureKey]) {
                    result = -1;
                }
                if (!ascending) {
                    result = -1 * result;
                }
                return result;
            }),
    };
};

export const combineCollections = (collections) => {
    const newUuid = getUUID();
    const newLine = {
        type: 'Feature',
        properties: {
            ...defaultGeoFeatureProperties.LineString,
            trailUUID: newUuid,
            id: newUuid,
        },
        geometry: {
            type: 'LineString',
            coordinates: [],
        },
    };
    let newFeatures = [];
    collections.forEach((collection) => {
        // Poinst
        const points = getPoints(collection);
        points.forEach((point) => {
            newFeatures.push({
                ...point,
                properties: {
                    ...point.properties,
                    elevGain:
                        newLine.properties.elevGain + point.properties.elevGain,
                    elevLoss:
                        newLine.properties.elevLoss + point.properties.elevLoss,
                    odometer:
                        newLine.properties.distance + point.properties.odometer,
                },
            });
        });
        // Lines
        const lines = getLines(collection);
        lines.forEach((line) => {
            newLine.geometry.coordinates = [
                ...newLine.geometry.coordinates,
                ...line.geometry.coordinates,
            ];
            // Slug
            const newSlug = newLine.properties.trailSlug.split('-').shift();
            if (newSlug && newSlug.length) {
                newLine.properties.trailSlug += `-${newSlug.join('-')}`;
            }
            // Name
            newLine.properties.trailName += `, ${line.properties.trailName}`;
            newLine.properties.trailNameEn += `, ${line.properties.trailNameEn}`;
            // Description
            newLine.properties.trailDesc += `\n${line.properties.trailDesc}`;
            newLine.properties.trailDescEn += `\n${line.properties.trailDescEn}`;
            // TypeID
            newLine.properties.typeID = Math.max(
                newLine.properties.typeID,
                line.properties.typeID,
            );
            // surfaceCollection - intentionaly before distance
            const mewSurfaceCollection = line.properties.surfaceCollection.map(
                (change) => {
                    const { key, value } = change;
                    return {
                        key: newLine.properties.distance + key,
                        value,
                    };
                },
            );
            newLine.properties.surfaceCollection = [
                ...newLine.properties.surfaceCollection,
                ...mewSurfaceCollection,
            ].sort((a, b) => a.key - b.key);
            // Distance
            newLine.properties.distance += line.properties.distance;
            // elevMin
            newLine.properties.elevMin = Math.min(
                newLine.properties.elevMin,
                line.properties.elevMin,
            );
            // elevMax
            newLine.properties.elevMax = Math.max(
                newLine.properties.elevMax,
                line.properties.elevMax,
            );
            // elevGain
            newLine.properties.elevGain += line.properties.elevGain;
            // elevLoss
            newLine.properties.elevLoss += line.properties.elevLoss;
            // reviewLandscape
            newLine.properties.reviewLandscape = Math.max(
                newLine.properties.reviewLandscape,
                line.properties.reviewLandscape,
            );
            // reviewLandscape
            newLine.properties.reviewFun = Math.max(
                newLine.properties.reviewFun,
                line.properties.reviewFun,
            );
            // reviewLandscape
            newLine.properties.requiredFitness = Math.max(
                newLine.properties.requiredFitness,
                line.properties.requiredFitness,
            );
            // reviewLandscape
            newLine.properties.requiredTechnique = Math.max(
                newLine.properties.requiredTechnique,
                line.properties.requiredTechnique,
            );
            // placeObjects
            line.properties.placeObjects.forEach((place) => {
                if (
                    !newLine.properties.placeObjects.some(
                        (p) => p.uuid === place.uuid,
                    )
                ) {
                    newLine.properties.placeObjects.push(place);
                }
            });
        });
    });
    newFeatures = [newLine, ...newFeatures];
    return turf.featureCollection(newFeatures);
};

export const getCollectionForExport = (collection) => {
    const features = collection.features.map((feature) => {
        const { properties, geometry } = feature;
        const { name, desc, trailName, trailDesc } = properties;
        let { coordinates, type } = geometry;
        if (type === 'Point') {
            if (coordinates.length > 2) {
                coordinates = [coordinates[0], coordinates[1]];
            }
        } else {
            coordinates = coordinates.map((coordinate) => {
                if (coordinate.length > 2) {
                    return [coordinate[0], coordinate[1]];
                }
                return coordinate;
            })
        }
        return {
            ...feature,
            properties: {
                name: name || trailName,
                desc: desc || trailDesc,
            },
            geometry: {
                type,
                coordinates,
            },
        };
    });
    return turf.featureCollection(features);
};
